import Axios from 'axios'
import { env } from './config/env'
import { Admin, AllowedEvent, Audit, AuthCheckResponse, AuthMethod, Campaign, CampaignApprovalRequestParams, CampaignCreateParams, CampaignLaunchParams, CampaignSendState, CampaignType, CampaignUpdateParams, CampaignUser, Export, Folder, FolderCreateParams, GlobalData, GlobalDataSummary, FolderSearchResult, FolderUpdateParams, Image, Journey, JourneyEntranceDetail, JourneyStepMap, JourneyUserStep, LaunchApproval, LaunchApprovalResponseParams, List, ListCreateParams, ListUpdateParams, Locale, Notification, Organization, OrganizationCreateParams, OrganizationUpdateParams, Project, ProjectAdmin, ProjectAdminInviteParams, ProjectAdminParams, ProjectApiKey, ProjectApiKeyParams, ProjectUsage, Provider, ProviderCreateParams, ProviderMeta, ProviderUpdateParams, QueueMetric, Resource, RulePath, SAMLConfigParams, SAMLConfigResponse, SESDomainStatus, SearchParams, TelnyxAvailableNumber, TelnyxMessagingStatus, TelnyxNumberSearchFilters, TelnyxSubUsecase, SearchResult, Series, StepError, Subscription, SubscriptionCreateParams, SubscriptionParams, SubscriptionUpdateParams, Tag, Template, TemplateCreateParams, TemplatePreviewParams, TemplateProofParams, TemplateUpdateParams, User, UserEvent, UserSubscription, UserUpdateParams, UserUpload, VariableSuggestions, MailchimpAudience, MailchimpImport, MailchimpImportParams, MailchimpTag, WebhookDelivery, WebhookSubscription, WebhookSubscriptionCreated, WebhookSubscriptionParams } from './types'

function appendValue(params: URLSearchParams, name: string, value: unknown) {
    if (typeof value === 'undefined' || value === null || typeof value === 'function') return
    if (typeof value === 'object') value = JSON.stringify(value)
    params.append(name, value + '')
}

export const client = Axios.create({
    ...env.api,
    paramsSerializer: params => {
        const s = new URLSearchParams()
        for (const [name, value] of Object.entries(params)) {
            if (Array.isArray(value)) {
                for (const item of value) {
                    appendValue(s, name, item)
                }
            } else {
                appendValue(s, name, value)
            }
        }
        return s.toString()
    },
})

client.interceptors.response.use(
    response => response,
    async error => {
        if (error.response.status === 401) {
            api.auth.login()
        }
        throw error
    },
)

export interface NetworkError {
    response: {
        data: any
        status: number
    }
}

type OmitFields = 'id' | 'created_at' | 'updated_at' | 'deleted_at' | 'stats' | 'stats_at'

export interface EntityApi<T> {
    basePath: string
    search: (params: Partial<SearchParams>) => Promise<SearchResult<T>>
    create: (params: Omit<T, OmitFields>) => Promise<T>
    get: (id: number | string) => Promise<T>
    update: (id: number | string, params: Omit<T, OmitFields>) => Promise<T>
    delete: (id: number | string) => Promise<number>
}

function createEntityPath<T>(basePath: string): EntityApi<T> {
    return {
        basePath,
        search: async params => await client
            .get<SearchResult<T>>(basePath, { params })
            .then(r => r.data),
        create: async params => await client
            .post<T>(basePath, params)
            .then(r => r.data),
        get: async id => await client
            .get<T>(`${basePath}/${id}`)
            .then(r => r.data),
        update: async (id, params) => await client
            .patch<T>(`${basePath}/${id}`, params)
            .then(r => r.data),
        delete: async id => await client
            .delete<number>(`${basePath}/${id}`)
            .then(r => r.data),
    }
}

export interface ProjectEntityPath<
    T, // Base Type
    C = Omit<T, OmitFields>, // Create Type
    U = Omit<T, OmitFields>, // Update Type
    S = SearchResult<T>, // Search Type
> {
    prefix: string
    search: (projectId: number | string, params: SearchParams) => Promise<S>
    create: (projectId: number | string, params: C) => Promise<T>
    get: (projectId: number | string, id: number | string) => Promise<T>
    update: (projectId: number | string, id: number | string, params: U) => Promise<T>
    delete: (projectId: number | string, id: number | string) => Promise<number>
}

const projectUrl = (projectId: number | string) => `/admin/projects/${projectId}`
export const apiUrl = (projectId: number | string, path: string) => `${env.api.baseURL}/admin/projects/${projectId}/${path}`

function createProjectEntityPath<
    T, // Base Type
    C = Omit<T, OmitFields>, // Create Type
    U = Omit<T, OmitFields>, // Update Type
    S = SearchResult<T>, // Search Type
>(prefix: string): ProjectEntityPath<T, C, U, S> {
    return {
        prefix,
        search: async (projectId, params) => await client
            .get<S>(`${projectUrl(projectId)}/${prefix}`, { params })
            .then(r => r.data),
        create: async (projectId, params) => await client
            .post<T>(`${projectUrl(projectId)}/${prefix}`, params)
            .then(r => r.data),
        get: async (projectId, entityId) => await client
            .get<T>(`${projectUrl(projectId)}/${prefix}/${entityId}`)
            .then(r => r.data),
        update: async (projectId, entityId, params) => await client
            .patch<T>(`${projectUrl(projectId)}/${prefix}/${entityId}`, params)
            .then(r => r.data),
        delete: async (projectId, entityId) => await client
            .delete<number>(`${projectUrl(projectId)}/${prefix}/${entityId}`)
            .then(r => r.data),
    }
}

const cache: {
    profile: null | Admin
} = {
    profile: null,
}

const api = {

    auth: {
        methods: async () => await client
            .get<AuthMethod[]>('/auth/methods')
            .then(r => r.data),
        check: async (email: string) => await client
            .post<AuthCheckResponse>('/auth/check', { email })
            .then(r => r.data),
        basicAuth: async (email: string, password: string, redirect: string = '/') => {
            await client.post('/auth/login/basic/callback', { email, password })
            window.location.href = redirect
        },
        emailAuth: async (email: string, redirect: string = '/') => {
            await client.post('/auth/login/email', { email, redirect })
        },
        login() {
            window.location.href = `/login?r=${encodeURIComponent(window.location.href)}`
        },
        async logout() {
            window.location.href = env.api.baseURL + '/auth/logout'
        },
    },

    profile: {
        get: async () => {
            if (!cache.profile) {
                cache.profile = await client.get<Admin>('/admin/profile').then(r => r.data)
            }
            return cache.profile!
        },
    },

    mcpAuth: {
        getSession: async (sessionId: string) => await client
            .get(`/mcp-auth/authorize/session/${sessionId}`)
            .then(r => r.data),
        decide: async (params: { session: string, project_id?: number, approve: boolean }) => await client
            .post<{ redirect_url: string }>('/mcp-auth/authorize/decision', params)
            .then(r => r.data),
    },

    admins: createEntityPath<Admin>('/admin/organizations/admins'),

    projects: {
        ...createEntityPath<Project>('/admin/projects'),
        all: async () => await client
            .get<Project[]>('/admin/projects/all')
            .then(r => r.data),
        patch: async (projectId: number | string, params: Partial<Project>) => await client
            .patch<Project>(`/admin/projects/${projectId}`, params)
            .then(r => r.data),
        pathSuggestions: async (projectId: number | string, campaignId?: number) => await client
            .get<VariableSuggestions>(`${projectUrl(projectId)}/data/paths`, campaignId ? { params: { campaignId } } : undefined)
            .then(r => r.data),
        usage: async (projectId: number | string, year?: number) => await client
            .get<ProjectUsage>(`${projectUrl(projectId)}/usage`, year ? { params: { year } } : undefined)
            .then(r => r.data),
    },

    data: {
        userPaths: {
            search: async (projectId: number | string, params: SearchParams) => await client
                .get<SearchResult<RulePath>>(`${projectUrl(projectId)}/data/paths/users`, { params })
                .then(r => r.data),
            update: async (projectId: number | string, entityId: number | string, params: Partial<RulePath>) => await client
                .put<RulePath>(`${projectUrl(projectId)}/data/paths/users/${entityId}`, params)
                .then(r => r.data),
        },
        allowedEvents: {
            search: async (projectId: number | string, params: SearchParams) => await client
                .get<SearchResult<AllowedEvent>>(`${projectUrl(projectId)}/data/events`, { params })
                .then(r => r.data),
            create: async (projectId: number | string, name: string) => await client
                .post<AllowedEvent>(`${projectUrl(projectId)}/data/events`, { name })
                .then(r => r.data),
            delete: async (projectId: number | string, eventId: number | string) => await client
                .delete(`${projectUrl(projectId)}/data/events/${eventId}`)
                .then(r => r.data),
        },
        rebuild: async (projectId: number | string) => await client
            .post(`${projectUrl(projectId)}/data/paths/sync`)
            .then(r => r.data),
    },

    apiKeys: createProjectEntityPath<ProjectApiKey, ProjectApiKeyParams, Omit<ProjectApiKeyParams, 'scope'>>('keys'),

    webhookSubscriptions: {
        all: async (projectId: number | string) => await client
            .get<WebhookSubscription[]>(`${projectUrl(projectId)}/webhook_subscriptions`)
            .then(r => r.data),
        create: async (projectId: number | string, params: Partial<WebhookSubscriptionParams>) => await client
            .post<WebhookSubscriptionCreated>(`${projectUrl(projectId)}/webhook_subscriptions`, params)
            .then(r => r.data),
        update: async (projectId: number | string, id: number, params: Partial<WebhookSubscriptionParams>) => await client
            .patch<WebhookSubscription>(`${projectUrl(projectId)}/webhook_subscriptions/${id}`, params)
            .then(r => r.data),
        delete: async (projectId: number | string, id: number) => await client
            .delete(`${projectUrl(projectId)}/webhook_subscriptions/${id}`)
            .then(r => r.data),
        deliveries: async (projectId: number | string, id: number) => await client
            .get<WebhookDelivery[]>(`${projectUrl(projectId)}/webhook_subscriptions/${id}/deliveries`)
            .then(r => r.data),
    },

    campaigns: {
        ...createProjectEntityPath<Campaign, CampaignCreateParams, CampaignUpdateParams | CampaignLaunchParams, FolderSearchResult<Campaign>>('campaigns'),
        launch: async (projectId: number | string, campaignId: number | string, params: CampaignLaunchParams) => await client
            .patch(`${projectUrl(projectId)}/campaigns/${campaignId}/launch`, params)
            .then(r => r.data),
        users: async (projectId: number | string, campaignId: number | string, params: SearchParams) => await client
            .get<SearchResult<CampaignUser>>(`${projectUrl(projectId)}/campaigns/${campaignId}/users`, { params })
            .then(r => r.data),
        duplicate: async (projectId: number | string, campaignId: number | string, includeVariants = true, locales?: string[], type?: CampaignType) => await client
            .post<Campaign>(`${projectUrl(projectId)}/campaigns/${campaignId}/duplicate`, { include_variants: includeVariants, ...locales?.length && { locales }, ...type && { type } })
            .then(r => r.data),
        export: async (projectId: number | string, campaignId: number | string, states?: CampaignSendState[]) => await client
            .post<Export>(`${projectUrl(projectId)}/campaigns/${campaignId}/export`, states?.length ? { states } : {})
            .then(r => r.data),
        templates: async (projectId: number | string, campaignId: number | string, archived = false) => await client
            .get<Template[]>(`${projectUrl(projectId)}/campaigns/${campaignId}/templates`, { params: { archived } })
            .then(r => r.data),
        restore: async (projectId: number | string, campaignId: number | string) => await client
            .post<Campaign>(`${projectUrl(projectId)}/campaigns/${campaignId}/restore`)
            .then(r => r.data),
        size: async (projectId: number | string, campaignId: number | string) => await client
            .get<{ estimated_send_size: number }>(`${projectUrl(projectId)}/campaigns/${campaignId}/size`),
        history: async (projectId: number | string, campaignId: number | string) => await client
            .get<Audit[]>(`${projectUrl(projectId)}/campaigns/${campaignId}/history`)
            .then(r => r.data),
        requestApproval: async (projectId: number | string, campaignId: number | string, params: CampaignApprovalRequestParams) => await client
            .post<LaunchApproval>(`${projectUrl(projectId)}/campaigns/${campaignId}/request-approval`, params)
            .then(r => r.data),
        metrics: async (projectId: number | string, campaignId: number | string, start: string, end: string) => await client
            .get<{ sent: Series[], deliveryRate: Series[], opened: Series[], clicked: Series[], unsubscribed: Series[] }>(`${projectUrl(projectId)}/campaigns/${campaignId}/metrics`, { params: { start, end } })
            .then(r => r.data),
    },

    approvals: {
        ...createProjectEntityPath<LaunchApproval>('approvals'),
        approvers: async (projectId: number | string) => await client
            .get<ProjectAdmin[]>(`${projectUrl(projectId)}/approvals/approvers`)
            .then(r => r.data),
        approve: async (projectId: number | string, approvalId: number | string, params: LaunchApprovalResponseParams) => await client
            .post<LaunchApproval>(`${projectUrl(projectId)}/approvals/${approvalId}/approve`, params)
            .then(r => r.data),
        reject: async (projectId: number | string, approvalId: number | string, params: LaunchApprovalResponseParams) => await client
            .post<LaunchApproval>(`${projectUrl(projectId)}/approvals/${approvalId}/reject`, params)
            .then(r => r.data),
        cancel: async (projectId: number | string, approvalId: number | string) => await client
            .delete<LaunchApproval>(`${projectUrl(projectId)}/approvals/${approvalId}`)
            .then(r => r.data),
        requestApproval: async (projectId: number | string, campaignId: number | string, params: CampaignApprovalRequestParams) => await client
            .post<LaunchApproval>(`${projectUrl(projectId)}/campaigns/${campaignId}/request-approval`, params)
            .then(r => r.data),
    },

    journeys: {
        ...createProjectEntityPath<Journey, Omit<Journey, OmitFields>, Omit<Journey, OmitFields>, FolderSearchResult<Journey>>('journeys'),
        duplicate: async (projectId: number | string, journeyId: number | string) => await client
            .post<Campaign>(`${projectUrl(projectId)}/journeys/${journeyId}/duplicate`)
            .then(r => r.data),
        version: async (projectId: number | string, journeyId: number | string) => await client
            .post<Journey>(`${projectUrl(projectId)}/journeys/${journeyId}/version`)
            .then(r => r.data),
        publish: async (projectId: number | string, journeyId: number | string) => await client
            .post<Journey>(`${projectUrl(projectId)}/journeys/${journeyId}/publish`)
            .then(r => r.data),
        restore: async (projectId: number | string, journeyId: number | string) => await client
            .post<Journey>(`${projectUrl(projectId)}/journeys/${journeyId}/restore`)
            .then(r => r.data),
        steps: {
            get: async (projectId: number | string, journeyId: number | string) => await client
                .get<JourneyStepMap>(`/admin/projects/${projectId}/journeys/${journeyId}/steps`)
                .then(r => r.data),
            set: async (projectId: number | string, journeyId: number | string, stepData: JourneyStepMap) => await client
                .put<JourneyStepMap>(`/admin/projects/${projectId}/journeys/${journeyId}/steps`, stepData)
                .then(r => r.data),
            stats: async (projectId: number | string, journeyId: number | string) => await client
                .get<Record<number, { delay: number, action: number, completed: number, error: number }>>(`/admin/projects/${projectId}/journeys/${journeyId}/steps/stats`)
                .then(r => r.data),
            searchUsers: async (projectId: number | string, journeyId: number | string, stepId: number | string, params: SearchParams) => await client
                .get<SearchResult<JourneyUserStep>>(`/admin/projects/${projectId}/journeys/${journeyId}/steps/${stepId}/users`, { params })
                .then(r => r.data),
            errors: async (projectId: number | string, journeyId: number | string, stepId: number | string) => await client
                .get<StepError[]>(`/admin/projects/${projectId}/journeys/${journeyId}/steps/${stepId}/errors`)
                .then(r => r.data),
            sample: async (projectId: number | string, journeyId: number | string) => await client
                .get<JourneyStepMap>(`/admin/projects/${projectId}/journeys/${journeyId}/sample`)
                .then(r => r.data),
        },
        entrances: {
            search: async (projectId: number | string, journeyId: number | string, params: SearchParams) => await client
                .get<SearchResult<JourneyUserStep>>(`/admin/projects/${projectId}/journeys/${journeyId}/entrances`, { params })
                .then(r => r.data),
            log: async (projectId: number | string, entranceId: number | string) => await client
                .get<JourneyEntranceDetail>(`${projectUrl(projectId)}/journeys/entrances/${entranceId}`)
                .then(r => r.data),
        },
        users: {
            trigger: async (projectId: number | string, journeyId: number | string, entranceId: number | string, user: User) => await client
                .post<JourneyEntranceDetail>(`${projectUrl(projectId)}/journeys/${journeyId}/trigger`, { entrance_id: entranceId, user: { external_id: user.external_id } })
                .then(r => r.data),
            skipDelay: async (projectId: number | string, journeyId: number | string, userId: number | string, stepId: number | string) => await client
                .post<JourneyEntranceDetail>(`${projectUrl(projectId)}/journeys/${journeyId}/users/${userId}/steps/${stepId}/resume`)
                .then(r => r.data),
        },
    },

    templates: {
        ...createProjectEntityPath<Template, TemplateCreateParams, TemplateUpdateParams>('templates'),
        prerender: async (projectId: number | string, templateId: number | string) => await client.get<Template>(`${projectUrl(projectId)}/templates/${templateId}/prerender`).then(r => r.data),
        preview: async (projectId: number | string, templateId: number | string, params: TemplatePreviewParams) => await client.post(`${projectUrl(projectId)}/templates/${templateId}/preview`, params),
        proof: async (projectId: number | string, templateId: number | string, params: TemplateProofParams) => await client.post(`${projectUrl(projectId)}/templates/${templateId}/proof`, params),
        editor: async (projectId: number | string) => await client.post<any>(`${projectUrl(projectId)}/templates/editor`).then(r => r.data),
        convert: async (projectId: number | string, templateId: number | string) => await client.post<any>(`${projectUrl(projectId)}/templates/${templateId}/convert`).then(r => r.data),
        convertHtml: async (projectId: number | string, templateId: number | string) => await client.post<any>(`${projectUrl(projectId)}/templates/${templateId}/convert-html`).then(r => r.data),
        revert: async (projectId: number | string, templateId: number | string) => await client.post<any>(`${projectUrl(projectId)}/templates/${templateId}/revert`).then(r => r.data),
        unarchive: async (projectId: number | string, templateId: number | string) => await client.put<Template>(`${projectUrl(projectId)}/templates/${templateId}/unarchive`).then(r => r.data),
    },

    users: {
        ...createProjectEntityPath<User, User, UserUpdateParams>('users'),
        lists: async (projectId: number | string, userId: number | string, params: SearchParams) => await client
            .get<SearchResult<List>>(`${projectUrl(projectId)}/users/${userId}/lists`, { params })
            .then(r => r.data),
        events: async (projectId: number | string, userId: number | string, params: SearchParams) => await client
            .get<SearchResult<UserEvent>>(`${projectUrl(projectId)}/users/${userId}/events`, { params })
            .then(r => r.data),
        subscriptions: async (projectId: number | string, userId: number | string, params: SearchParams) => await client
            .get<SearchResult<UserSubscription>>(`${projectUrl(projectId)}/users/${userId}/subscriptions`, { params })
            .then(r => r.data),
        updateSubscriptions: async (projectId: number | string, userId: number | string, subscriptions: SubscriptionParams[]) => await client
            .patch(`${projectUrl(projectId)}/users/${userId}/subscriptions`, subscriptions)
            .then(r => r.data),
        notifications: async (projectId: number | string, userId: number | string, params: SearchParams) => await client
            .get<SearchResult<Notification>>(`${projectUrl(projectId)}/users/${userId}/notifications`, { params })
            .then(r => r.data),
        readNotification: async (projectId: number | string, userId: number | string, notificationId: number) => await client
            .post<Notification>(`${projectUrl(projectId)}/users/${userId}/notifications/${notificationId}/read`)
            .then(r => r.data),
        query: async (projectId: number | string, params: { rule: any, cursor?: string, limit?: number }) => await client
            .post<SearchResult<User>>(`${projectUrl(projectId)}/users/query`, params)
            .then(r => r.data),
        deleteImport: async (projectId: number | string, file: File) => {
            const formData = new FormData()
            formData.append('file', file)
            await client.post(`${projectUrl(projectId)}/users/delete`, formData)
        },

        journeys: {
            search: async (projectId: number | string, userId: number | string, params: SearchParams) => await client
                .get<SearchResult<JourneyUserStep>>(`${projectUrl(projectId)}/users/${userId}/journeys`, { params })
                .then(r => r.data),
        },

        uploads: {
            create: async (projectId: number | string, file: File, onProgress?: (progress: number) => void) => {
                const formData = new FormData()
                formData.append('file', file)
                return await client.post<UserUpload>(`${projectUrl(projectId)}/users/uploads`, formData, {
                    onUploadProgress: (event) => {
                        if (onProgress && event.total) {
                            onProgress(Math.round((event.loaded / event.total) * 100))
                        }
                    },
                }).then(r => r.data)
            },
            get: async (projectId: number | string, uploadId: number | string) => await client
                .get<UserUpload>(`${projectUrl(projectId)}/users/uploads/${uploadId}`)
                .then(r => r.data),
            updateMapping: async (projectId: number | string, uploadId: number | string, params: { column_mapping: Record<string, string>, list_id?: number }) => await client
                .patch<UserUpload>(`${projectUrl(projectId)}/users/uploads/${uploadId}`, params)
                .then(r => r.data),
            process: async (projectId: number | string, uploadId: number | string) => await client
                .post<UserUpload>(`${projectUrl(projectId)}/users/uploads/${uploadId}/process`)
                .then(r => r.data),
        },
    },

    mailchimp: {
        audiences: async (projectId: number | string, apiKey: string) => await client
            .post<MailchimpAudience[]>(`${projectUrl(projectId)}/mailchimp/audiences`, { api_key: apiKey })
            .then(r => r.data),
        tags: async (projectId: number | string, apiKey: string, audienceId: string) => await client
            .post<MailchimpTag[]>(`${projectUrl(projectId)}/mailchimp/tags`, { api_key: apiKey, audience_id: audienceId })
            .then(r => r.data),
        imports: {
            create: async (projectId: number | string, params: MailchimpImportParams) => await client
                .post<MailchimpImport>(`${projectUrl(projectId)}/mailchimp/imports`, params)
                .then(r => r.data),
            get: async (projectId: number | string, id: number | string) => await client
                .get<MailchimpImport>(`${projectUrl(projectId)}/mailchimp/imports/${id}`)
                .then(r => r.data),
        },
    },

    lists: {
        ...createProjectEntityPath<List, ListCreateParams, ListUpdateParams, FolderSearchResult<List>>('lists'),
        users: async (projectId: number | string, listId: number | string, params: SearchParams) => await client
            .get<SearchResult<User>>(`${projectUrl(projectId)}/lists/${listId}/users`, { params })
            .then(r => r.data),
        upload: async (projectId: number | string, listId: number | string, file: File) => {
            const formData = new FormData()
            formData.append('file', file)
            await client.post(`${projectUrl(projectId)}/lists/${listId}/users`, formData)
        },
        duplicate: async (projectId: number | string, listId: number | string) => await client
            .post<List>(`${projectUrl(projectId)}/lists/${listId}/duplicate`)
            .then(r => r.data),
        export: async (projectId: number | string, listId: number | string) => await client
            .post<Export>(`${projectUrl(projectId)}/lists/${listId}/export`)
            .then(r => r.data),
        deleteUsers: async (projectId: number | string, listId: number | string) => await client
            .delete(`${projectUrl(projectId)}/lists/${listId}/users`),
        subscribeUsers: async (projectId: number | string, listId: number | string, params: { subscription_id: number, trigger_journeys: boolean }) => await client
            .post(`${projectUrl(projectId)}/lists/${listId}/subscriptions`, params),
    },

    exports: {
        search: async (projectId: number | string, params: SearchParams) => await client
            .get<SearchResult<Export>>(`${projectUrl(projectId)}/exports`, { params })
            .then(r => r.data),
        get: async (projectId: number | string, exportId: number | string) => await client
            .get<Export>(`${projectUrl(projectId)}/exports/${exportId}`)
            .then(r => r.data),

        // The endpoint redirects to a short-lived storage link, so the browser
        // has to follow it directly rather than pulling the file through axios.
        downloadUrl: (projectId: number | string, exportId: number | string) =>
            apiUrl(projectId, `exports/${exportId}/download`),
    },

    projectAdmins: {
        search: async (projectId: number, params: SearchParams) => await client
            .get<SearchResult<ProjectAdmin>>(`${projectUrl(projectId)}/admins`, { params })
            .then(r => r.data),
        add: async (projectId: number, adminId: number, params: ProjectAdminParams) => await client
            .put<ProjectAdmin>(`${projectUrl(projectId)}/admins/${adminId}`, params)
            .then(r => r.data),
        invite: async (projectId: number, params: ProjectAdminInviteParams) => await client
            .post<ProjectAdmin>(`${projectUrl(projectId)}/admins`, params)
            .then(r => r.data),
        get: async (projectId: number, adminId: number) => await client
            .get<ProjectAdmin>(`${projectUrl(projectId)}/admins/${adminId}`)
            .then(r => r.data),
        remove: async (projectId: number, adminId: number) => await client
            .delete(`${projectUrl(projectId)}/admins/${adminId}`)
            .then(r => r.data),
    },

    subscriptions: createProjectEntityPath<Subscription, SubscriptionCreateParams, SubscriptionUpdateParams>('subscriptions'),

    providers: {
        all: async (projectId: number | string) => await client
            .get<Provider[]>(`${projectUrl(projectId)}/providers/all`)
            .then(r => r.data),
        search: async (projectId: number | string, params: any) => await client
            .get<SearchResult<Provider>>(`${projectUrl(projectId)}/providers`, { params })
            .then(r => r.data),
        options: async (projectId: number | string) => await client
            .get<ProviderMeta[]>(`${projectUrl(projectId)}/providers/meta`)
            .then(r => r.data),
        get: async (projectId: number | string, group: string, type: string, entityId: number | string) => await client
            .get<Provider>(`${projectUrl(projectId)}/providers/${group}/${type}/${entityId}`)
            .then(r => r.data),
        create: async (projectId: number | string, { group, type, ...provider }: ProviderCreateParams) => await client
            .post<Provider>(`${projectUrl(projectId)}/providers/${group}/${type}`, provider)
            .then(r => r.data),
        update: async (projectId: number | string, entityId: number | string, { group, type, ...provider }: ProviderUpdateParams) => await client
            .patch<Provider>(`${projectUrl(projectId)}/providers/${group}/${type}/${entityId}`, provider)
            .then(r => r.data),
        delete: async (projectId: number | string, id: number) => await client
            .delete<number>(`${projectUrl(projectId)}/providers/${id}`)
            .then(r => r.data),
        copy: async (projectId: number | string, id: number, targetProjectId: number) => await client
            .post<Provider>(`${projectUrl(projectId)}/providers/${id}/copy`, { project_id: targetProjectId })
            .then(r => r.data),
        getDomain: async (projectId: number | string, group: string, type: string, entityId: number | string) => await client
            .get<SESDomainStatus>(`${projectUrl(projectId)}/providers/${group}/${type}/${entityId}/domain`)
            .then(r => r.data),
        createDomain: async (projectId: number | string, group: string, type: string, entityId: number | string, domain: string) => await client
            .post<SESDomainStatus>(`${projectUrl(projectId)}/providers/${group}/${type}/${entityId}/domain`, { domain })
            .then(r => r.data),
        checkDomain: async (projectId: number | string, group: string, type: string, entityId: number | string) => await client
            .post<SESDomainStatus>(`${projectUrl(projectId)}/providers/${group}/${type}/${entityId}/domain/check`)
            .then(r => r.data),
        getMessagingStatus: async (projectId: number | string, group: string, type: string, entityId: number | string) => await client
            .get<TelnyxMessagingStatus>(`${projectUrl(projectId)}/providers/${group}/${type}/${entityId}/10dlc/status`)
            .then(r => r.data),
        createBrand: async (projectId: number | string, group: string, type: string, entityId: number | string, brandData: Record<string, any>) => await client
            .post<TelnyxMessagingStatus>(`${projectUrl(projectId)}/providers/${group}/${type}/${entityId}/10dlc/brand`, brandData)
            .then(r => r.data),
        checkBrand: async (projectId: number | string, group: string, type: string, entityId: number | string) => await client
            .post<TelnyxMessagingStatus>(`${projectUrl(projectId)}/providers/${group}/${type}/${entityId}/10dlc/brand/check`)
            .then(r => r.data),
        getUsecases: async (projectId: number | string, group: string, type: string, entityId: number | string) => await client
            .get<TelnyxSubUsecase[]>(`${projectUrl(projectId)}/providers/${group}/${type}/${entityId}/10dlc/usecases`)
            .then(r => r.data),
        createCampaign: async (projectId: number | string, group: string, type: string, entityId: number | string, campaignData: Record<string, any>) => await client
            .post<TelnyxMessagingStatus>(`${projectUrl(projectId)}/providers/${group}/${type}/${entityId}/10dlc/campaign`, campaignData)
            .then(r => r.data),
        checkCampaign: async (projectId: number | string, group: string, type: string, entityId: number | string) => await client
            .post<TelnyxMessagingStatus>(`${projectUrl(projectId)}/providers/${group}/${type}/${entityId}/10dlc/campaign/check`)
            .then(r => r.data),
        searchNumbers: async (projectId: number | string, group: string, type: string, entityId: number | string, filters: TelnyxNumberSearchFilters) => await client
            .get<TelnyxAvailableNumber[]>(`${projectUrl(projectId)}/providers/${group}/${type}/${entityId}/10dlc/numbers/available`, { params: filters })
            .then(r => r.data),
        purchaseNumber: async (projectId: number | string, group: string, type: string, entityId: number | string, phoneNumber: string) => await client
            .post<TelnyxMessagingStatus>(`${projectUrl(projectId)}/providers/${group}/${type}/${entityId}/10dlc/numbers/purchase`, { phone_number: phoneNumber })
            .then(r => r.data),
        checkNumber: async (projectId: number | string, group: string, type: string, entityId: number | string) => await client
            .post<TelnyxMessagingStatus>(`${projectUrl(projectId)}/providers/${group}/${type}/${entityId}/10dlc/numbers/check`)
            .then(r => r.data),
        resetMessaging: async (projectId: number | string, group: string, type: string, entityId: number | string) => await client
            .post<TelnyxMessagingStatus>(`${projectUrl(projectId)}/providers/${group}/${type}/${entityId}/10dlc/reset`)
            .then(r => r.data),
    },

    slack: {
        getOAuthUrl: async (projectId: number | string) => await client
            .get<{ url: string }>(`${projectUrl(projectId)}/providers/slack/slack/oauth/start`)
            .then(r => r.data),
    },

    images: {
        ...createProjectEntityPath<Image>('images'),
        create: async (projectId: number | string, image: File) => {
            const formData = new FormData()
            formData.append('image', image)
            return await client.post<Image>(`${projectUrl(projectId)}/images`, formData)
                .then(r => r.data)
        },
    },

    globals: {
        all: async (projectId: number | string) => await client
            .get<GlobalDataSummary[]>(`${projectUrl(projectId)}/globals`)
            .then(r => r.data),
        get: async (projectId: number | string, key: string) => await client
            .get<GlobalData>(`${projectUrl(projectId)}/globals/${encodeURIComponent(key)}`)
            .then(r => r.data),
    },

    resources: {
        all: async (projectId: number | string, type?: string) => await client
            .get<Resource[]>(`${projectUrl(projectId)}/resources${type ? `?type=${type}` : ''}`)
            .then(r => r.data),
        create: async (projectId: number | string, params: Partial<Resource>) => await client
            .post<Resource>(`${projectUrl(projectId)}/resources`, params)
            .then(r => r.data),
        update: async (projectId: number | string, id: number, params: Partial<Resource>) => await client
            .patch<Resource>(`${projectUrl(projectId)}/resources/${id}`, params)
            .then(r => r.data),
        delete: async (projectId: number | string, id: number) => await client
            .delete<number>(`${projectUrl(projectId)}/resources/${id}`)
            .then(r => r.data),
    },

    tags: {
        ...createProjectEntityPath<Tag>('tags'),
        used: async (projectId: number | string, entity: string) => await client
            .get<Tag[]>(`${projectUrl(projectId)}/tags/used/${entity}`)
            .then(r => r.data),
        assign: async (projectId: number | string, entity: string, entityId: number, tags: string[]) => await client
            .put<string[]>(`${projectUrl(projectId)}/tags/assign`, { entity, entityId, tags })
            .then(r => r.data),
        all: async (projectId: number | string) => await client
            .get<Tag[]>(`${projectUrl(projectId)}/tags/all`)
            .then(r => r.data),
    },

    folders: {
        ...createProjectEntityPath<Folder, Omit<FolderCreateParams, OmitFields>, Omit<FolderUpdateParams, OmitFields>>('folders'),
    },

    organizations: {
        get: async () => await client
            .get<Organization>('/admin/organizations')
            .then(r => r.data),
        all: async () => await client
            .get<Organization[]>('/admin/organizations/all')
            .then(r => r.data),
        create: async (params: OrganizationCreateParams) => await client
            .post<Organization>('/admin/organizations', params)
            .then(r => r.data),
        switch: async (organizationId: number) => {
            await client.post('/admin/organizations/switch', { organization_id: organizationId })
            window.location.href = '/organization'
        },
        update: async (id: number | string, params: OrganizationUpdateParams) => await client
            .patch<Organization>(`/admin/organizations/${id}`, params)
            .then(r => r.data),
        delete: async () => await client
            .delete('/admin/organizations')
            .then(r => r.data),
        metrics: async () => await client
            .get<QueueMetric>('/admin/organizations/performance/queue')
            .then(r => r.data),
        getQueueState: async (queueName: string) => await client
            .get<boolean>(`/admin/organizations/performance/queue/${queueName}/state`)
            .then(r => r.data),
        setQueueState: async (queueName: string) => await client
            .put<boolean>(`/admin/organizations/performance/queue/${queueName}/state`)
            .then(r => r.data),
        jobs: async () => await client
            .get<string[]>('/admin/organizations/performance/jobs')
            .then(r => r.data),
        jobPerformance: async (job: string) => await client
            .get<{ throughput: Series[], timing: Series[] }>(`/admin/organizations/performance/jobs/${job}`)
            .then(r => r.data),
        failed: async () => await client
            .get<any>('/admin/organizations/performance/failed')
            .then(r => r.data),
        clearDeduplicationKey: async (key: string) => await client
            .post<string>('/admin/organizations/queue/clear', { key })
            .then(r => r.data),
        clearQueue: async (queueName: string) => await client
            .post<boolean>(`/admin/organizations/performance/queue/${queueName}/clear`)
            .then(r => r.data),
        getSamlConfig: async () => await client
            .get<SAMLConfigResponse>('/admin/organizations/saml')
            .then(r => r.data),
        updateSamlConfig: async (params: SAMLConfigParams) => await client
            .put<Organization>('/admin/organizations/saml', params)
            .then(r => r.data),
        deleteSamlConfig: async () => await client
            .delete('/admin/organizations/saml')
            .then(r => r.data),
    },

    locales: createProjectEntityPath<Locale>('locales'),

    events: {
        search: async (projectId: number | string, params: SearchParams) => await client
            .get<SearchResult<UserEvent>>(`${projectUrl(projectId)}/events`, { params })
            .then(r => r.data),
    },
}

export default api;

(window as any).API = api
